import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Facebook, Twitter, Instagram, Youtube, 
  Mail, Phone, MapPin, Send
} from 'lucide-react';
import { useNewspaper } from '../../context/AppContext';
import { configService } from '../../services/api';
import styles from './Footer.module.css';

export default function Footer() {
  const { config, categories } = useNewspaper();
  const [director, setDirector] = useState(null);

  const currentYear = new Date().getFullYear();

  // Carga el Director del periódico (endpoint público, sin auth).
  // Si no hay Director configurado, el backend devuelve null y
  // simplemente no se renderiza el bloque.
  useEffect(() => {
    let mounted = true;
    configService.getDirectorPublico()
      .then(d => { if (mounted) setDirector(d); })
      .catch(err => console.warn('Director:', err.message));
    return () => { mounted = false; };
  }, []);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    // Integrar con backend
    alert('¡Gracias por suscribirte!');
  };

  return (
    <footer className={styles.footer}>
      {/* Newsletter */}
      <div className={styles.newsletter}>
        <div className="container">
          <div className={styles.newsletterContent}>
            <div className={styles.newsletterText}>
              <h3 className={styles.newsletterTitle}>
                Recibe las noticias más importantes
              </h3>
              <p className={styles.newsletterDescription}>
                Suscríbete a nuestro boletín diario y mantente informado
              </p>
            </div>
            <form onSubmit={handleNewsletterSubmit} className={styles.newsletterForm}>
              <input
                type="email"
                placeholder="Tu correo electrónico"
                className={styles.newsletterInput}
                required
              />
              <button type="submit" className={styles.newsletterButton}>
                <Send size={18} />
                Suscribirse
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Contenido principal del footer */}
      <div className={styles.main}>
        <div className="container">
          <div className={styles.grid}>
            {/* Información del periódico */}
            <div className={styles.about}>
              <Link to="/" className={styles.logo}>
                {config?.name || ''}
              </Link>
              <p className={styles.slogan}>
                {config?.description || ''}
              </p>
              <p className={styles.description}>
                Fundado en {config?.foundedYear || 1920}, somos el periódico más 
                leído de la región. Comprometidos con el periodismo de calidad y 
                la verdad.
              </p>
              <div className={styles.social}>
                <a href="#" className={styles.socialLink} aria-label="Facebook">
                  <Facebook size={20} />
                </a>
                <a href="#" className={styles.socialLink} aria-label="Twitter">
                  <Twitter size={20} />
                </a>
                <a href="#" className={styles.socialLink} aria-label="Instagram">
                  <Instagram size={20} />
                </a>
                <a href="#" className={styles.socialLink} aria-label="YouTube">
                  <Youtube size={20} />
                </a>
              </div>

              {/* Director del periódico — cargo editorial. Solo si está
                  configurado en Parámetros editoriales del admin. */}
              {director && (
                <div className={styles.director}>
                  <span className={styles.directorLabel}>Director</span>
                  <Link to={`/autor/${director.id}`} className={styles.directorLink}>
                    {director.avatarUrl ? (
                      <img
                        src={director.avatarUrl}
                        alt={director.nombre}
                        className={styles.directorAvatar}
                      />
                    ) : (
                      <span className={styles.directorAvatarFallback}>
                        {(director.nombre || '?').charAt(0).toUpperCase()}
                      </span>
                    )}
                    <span className={styles.directorInfo}>
                      <span className={styles.directorName}>{director.nombre}</span>
                      {director.cargo && (
                        <span className={styles.directorRole}>{director.cargo}</span>
                      )}
                    </span>
                  </Link>
                </div>
              )}
            </div>

            {/* Secciones */}
            <div className={styles.linksGroup}>
              <h4 className={styles.linksTitle}>Secciones</h4>
              <ul className={styles.links}>
                {categories.slice(0, 6).map(category => (
                  <li key={category.id}>
                    <Link to={`/seccion/${category.slug}`} className={styles.link}>
                      {category.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Más secciones */}
            <div className={styles.linksGroup}>
              <h4 className={styles.linksTitle}>Más</h4>
              <ul className={styles.links}>
                {categories.slice(6).map(category => (
                  <li key={category.id}>
                    <Link to={`/seccion/${category.slug}`} className={styles.link}>
                      {category.name}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link to="/ediciones" className={styles.link}>
                    Ediciones impresas
                  </Link>
                </li>
                <li>
                  <Link to="/archivo" className={styles.link}>
                    Archivo histórico
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contacto */}
            <div className={styles.contact}>
              <h4 className={styles.linksTitle}>Contacto</h4>
              <ul className={styles.contactList}>
                <li className={styles.contactItem}>
                  <MapPin size={16} />
                  <span>{config?.contact?.address || 'Calle 36 #15-23, Bucaramanga'}</span>
                </li>
                <li className={styles.contactItem}>
                  <Phone size={16} />
                  <span>{config?.contact?.phone || '+57 7 123 4567'}</span>
                </li>
                <li className={styles.contactItem}>
                  <Mail size={16} />
                  <a href={`mailto:${config?.contact?.email || 'contacto@eldiario.com'}`}>
                    {config?.contact?.email || 'contacto@eldiario.com'}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Barra inferior */}
      <div className={styles.bottom}>
        <div className="container">
          <div className={styles.bottomContent}>
            <p className={styles.copyright}>
              © {currentYear} {config?.name || ''}. 
              Todos los derechos reservados.
            </p>
            <nav className={styles.bottomNav}>
              <Link to="/terminos">Términos de uso</Link>
              <Link to="/privacidad">Política de privacidad</Link>
              <Link to="/cookies">Cookies</Link>
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
}
